import { faCircleExclamation } from '@fortawesome/pro-solid-svg-icons';
import Box from '@mui/system/Box';
import { SelectFieldOption } from '@resurs-internal/mui/components/SelectField/SelectFieldOption';
import { useEffect, useState } from 'react';

import Description from '../../components/Description/Description';
import Group from '../../components/Group/Group';
import IconWithText from '../../components/IconWithText/IconWithText';
import MonetaryField from '../../components/MonetaryField/MonetaryField';
import NumericField from '../../components/NumericField/NumericField';
import PagesFooter from '../../components/PagesFooter/PagesFooter';
import PagesHeader from '../../components/PagesHeader/PagesHeader';
import RadioField from '../../components/RadioField/RadioField';
import SelectField from '../../components/SelectField/SelectField';
import ShowDetailsModal from '../../components/ShowDetailsModal/ShowDetailsModal';
import Subtitle from '../../components/Subtitle/Subtitle';
import Title from '../../components/Title/Title';
import { usePagesContext } from '../../context/PagesContext';
import { scrollToFieldWithError } from '../../helpers/scrollToFieldWithError';
import { useAppDispatch, useAppSelector } from '../../hooks';
import useComponentTracker from '../../hooks/useComponentTracker';
import useDefaultPagesAction from '../../hooks/useDefaultPagesAction';
import useFieldToggle from '../../hooks/useFieldToggle';
import { ValidationResultType } from '../../hooks/useFieldValidation';
import usePagesConfig from '../../hooks/usePagesConfig';
import useSetBannerType from '../../hooks/useSetBannerType';
import useTranslation from '../../hooks/useTranslation';
import useValidation from '../../hooks/useValidation';
import { addOrUpdateFormField } from '../../store/applicationform';
import { selectFieldValue } from '../../store/applicationform/applicationFormSelectors';
import { WizardPageAction } from '../../store/wizard/wizard';

const yesNoOptions = [
    {
        id: 'true',
        label: 'Ja',
    },
    {
        id: 'false',
        label: 'Nej',
    },
];

const shareOfExpensesOptions: SelectFieldOption[] = [
    { id: '0', label: '0%' },
    { id: '25', label: '25%' },
    { id: '50', label: '50%' },
    { id: '75', label: '75%' },
    { id: '100', label: '100%' },
];

const FinancePage = () => {
    useComponentTracker('FinancePage');

    const translate = useTranslation();
    const { totalSections, totalAboutYouPages } = usePagesConfig();
    const { currentPage } = usePagesContext();
    const defaultPagesAction = useDefaultPagesAction();
    const dispatch = useAppDispatch();

    useSetBannerType('bannerOne');

    const employmentType = useAppSelector(selectFieldValue)('applicantEmploymentType');
    const showPensionerNote = employmentType === 'PENSIONER';

    const applicantNewDebtThisYearToggle =
        useAppSelector(selectFieldValue)('applicantNewDebtThisYearToggle') === 'true';
    useFieldToggle(applicantNewDebtThisYearToggle, ['applicantNewDebtThisYear']);

    const [requiredFields, setRequiredFields] = useState<string[]>([]);
    useEffect(() => {
        const fields = [
            'applicantNetMonthlyIncome',
            'householdLivingExpenses',
            'householdLoanExpenses',
            'applicantShareOfExpenses',
            'applicantNumberOfCars',
            'applicantNewDebtThisYearToggle',
        ];
        if (applicantNewDebtThisYearToggle) {
            fields.push('applicantNewDebtThisYear');
        }
        setRequiredFields(fields);
    }, [applicantNewDebtThisYearToggle]);

    const validationErrors = useValidation(requiredFields);

    const onAction = async (action: WizardPageAction) => {
        if (action === 'next' && validationErrors.length > 0) {
            scrollToFieldWithError(validationErrors);
            validationErrors.forEach((validationError) => {
                dispatch(addOrUpdateFormField(validationError));
            });
            return false;
        }

        return defaultPagesAction(action);
    };

    const minValueValidation = async (value?: string): Promise<ValidationResultType> => {
        const isValid = Number(value ?? 0) > 0;

        // errorMessageKey is a translation key; it is resolved via translate() at display time.
        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : 'field.applicant.min-value.error',
        };
    };

    const nonNegativeValidation = async (value?: string): Promise<ValidationResultType> => {
        const isValid = Number(value ?? 0) >= 0;

        return {
            valid: isValid,
            errorMessageKey: isValid ? undefined : 'field.applicant.non-negative-value.error',
        };
    };

    return (
        <>
            <PagesHeader
                actions={['back']}
                onAction={onAction}
                currentPage={currentPage}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
            <Title text={translate('page.finance.title')} />
            <Description text={translate('page.finance.description')} />
            <ShowDetailsModal
                label={translate('dialog.know-more.label')}
                dialogTitle={translate('dialog.know-more.label')}
                dialogContent={translate('page.finance.dialog.content')}
            />
            <Group>
                <MonetaryField
                    name="applicantNetMonthlyIncome"
                    label={translate('field.applicant-net-monthly-income.label')}
                    customValidation={minValueValidation}
                />
                <MonetaryField
                    name="householdLivingExpenses"
                    label={translate('field.household-living-expenses.label')}
                    customValidation={minValueValidation}
                />
                <MonetaryField
                    name="householdLoanExpenses"
                    label={translate('field.household-loan-expenses.label')}
                    customValidation={nonNegativeValidation}
                />
                <NumericField
                    name="applicantNumberOfCars"
                    label={translate('field.applicant-number-of-cars.label')}
                    min={0}
                    max={20}
                />
            </Group>
            {showPensionerNote && (
                // This mb cancels the mt:3 below via CSS's mixed-sign margin collapse (the combined margin
                // becomes the largest positive value minus the largest negative magnitude, e.g. 24px - 24px =
                // 0), not IconWithText's own 16px padding-bottom -- that padding is real, non-collapsible
                // space and stays regardless. Without this, IconWithText's padding and the mt:3 below would
                // stack on top of each other, roughly doubling the gap versus when this note is absent.
                <Box sx={{ mb: -3 }}>
                    <IconWithText icon={faCircleExclamation} text="page.finance.body-text" iconSize="sm" />
                </Box>
            )}
            <Box sx={{ mt: 2 }}>
                <Subtitle text={translate('field.applicant-share-of-expenses.subtitle')} />
                <SelectField
                    label={translate('field.applicant-share-of-expenses.label')}
                    name="applicantShareOfExpenses"
                    options={shareOfExpensesOptions}
                />
            </Box>
            <Box sx={{ mt: 2 }}>
                <Subtitle text={translate('field.applicant-new-debt-this-year.label')} />
                <RadioField name="applicantNewDebtThisYearToggle" options={yesNoOptions} />
            </Box>
            {applicantNewDebtThisYearToggle && (
                <Box sx={{ mt: 1.5 }}>
                    <MonetaryField
                        name="applicantNewDebtThisYear"
                        label={translate('field.applicant-new-debt-this-year-amount.label')}
                        customValidation={minValueValidation}
                    />
                </Box>
            )}
            <PagesFooter
                actions={['next']}
                onAction={onAction}
                currentSection={0}
                numberOfSections={totalSections}
                numberOfPagesInSection={totalAboutYouPages}
                currentPageInSection={currentPage}
                sectionTitle={translate('field.section.about-you')}
            />
        </>
    );
};

export default FinancePage;
